import { memo, useCallback } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';

import { ModalProps } from './types';

import styles from './styles.module.scss';

import CloseIcon from '@/assets/icons/close.svg?react';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    position: 'absolute',
    minHeight: '205px',
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  padding: '8px 38x',
  border: 'none',
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '22px',
  position: 'relative',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  '& .MuiButtonBase-root': {
    position: 'static',
  },
});

const StyledDialogContent = styled(DialogContent)({
  margin: '0',
  overflow: 'hidden',
  wordWrap: 'break-word',
  position: 'relative',
  width: '100%',
  border: 'none',
  padding: 0,
  height: '100%',
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '22px',
  color: '#282828',
  '& .MuiDialogContent-root': {
    borderTop: '1px #eff2f7 solid',
    padding: '0 38px',
  },
});

const StyledDialogActions = styled(DialogActions)({
  padding: '24px 28px 28px',
  width: '100%',
  display: 'flex',
});

const StyledCloseButton = styled(IconButton)({
  color: '#7A89A7',
  marginRight: -10,
});

const StyledCancelButton = styled(Button)({
  height: '40px',
  border: '2px solid #EFF2F7',
  fontSize: '20px',
  '&:hover': {
    border: '2px solid #EFF2F7',
    filter: 'drop-shadow(0px 2px 15px rgba(122, 137, 167, 0.25))',
    borderRadius: '4px',
    background: 'white',
    color: '#282828',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    border: 'none',
    '& span': {
      color: '#00000042',
    },

    '&:hover': {
      cursor: 'not-allowed',
    },
  },
});

const StyledSubmitButton = styled(Button)({
  height: '40px',
  backgroundColor: '#52B36B',
  color: '#fff',
  '&:hover': {
    boxShadow: 'none',
    opacity: 0.8,
    backgroundColor: '#52B36B',
    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  },
  '&.Mui-disabled': {
    cursor: 'not-allowed',

    '&:hover': {
      cursor: 'not-allowed',
    },
  },
});

export const Modal = memo<ModalProps>(
  ({
    showSubmitBtnExternal,
    showCancelBtnExternal,
    showCloseBtnExternal,
    isOpenModal,
    children,
    title,
    onClose,
    onSubmit,
    disabledSubmitBtn,
    disabledCancelBtn,
    color,
    className,
    submitButtonText,
    cancelButtonText,
    onOverlayClick,
    disableOverlayClose,
    ...props
  }) => {
    const isSubmitBtnVisible = showSubmitBtnExternal ?? true;
    const isCancelBtnVisible = showCancelBtnExternal ?? true;
    const isCloseBtnVisible = showCloseBtnExternal ?? true;

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
      onSubmit?.();
    }, [onSubmit]);

    return (
      <StyledDialog
        {...props}
        open={isOpenModal}
        onClose={disableOverlayClose ? undefined : (onOverlayClick ?? onClose)}
        className={className}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <StyledDialogTitle id="alert-dialog-slide-title">
          <div className={styles.title}>{title}</div>

          {isCloseBtnVisible && (
            <StyledCloseButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </StyledCloseButton>
          )}
        </StyledDialogTitle>

        <StyledDialogContent>{children}</StyledDialogContent>

        {(isCancelBtnVisible || isSubmitBtnVisible) && (
          <StyledDialogActions>
            {isCancelBtnVisible && (
              <StyledCancelButton
                id="cancel-modal-btn"
                onClick={handleClose}
                size="large"
                variant="contained"
                color="secondary"
                disabled={disabledCancelBtn}
              >
                <span className={clsx(styles.btn, styles.btnBlue)}>{cancelButtonText || 'Cancel'}</span>
              </StyledCancelButton>
            )}

            {isSubmitBtnVisible && (
              <StyledSubmitButton
                id="submit-modal-btn"
                onClick={handleSubmit}
                size="large"
                disabled={disabledSubmitBtn}
                variant="contained"
                color={color || 'secondary'}
              >
                <span className={styles.btn}>{submitButtonText || 'Submit'}</span>
              </StyledSubmitButton>
            )}
          </StyledDialogActions>
        )}
      </StyledDialog>
    );
  },
);
